import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IChannelMappingResult, ChannelType } from '../../../types';
import { T_KEYS } from '../../../constants';

const ModalOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.48)',
  backdropFilter: 'blur(2.6px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ModalContainer = styled(Box)({
  backgroundColor: '#ffffff',
  borderRadius: '24px',
  padding: '32px',
  width: '526px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  position: 'relative',
});

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '16px',
  insetInlineEnd: '16px',
  color: '#666666',
});

const IconContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ErrorIconWrapper = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '28px',
  backgroundColor: 'rgba(225, 25, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const MessageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
  textAlign: 'center',
});

const Title = styled('p')({
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '28px',
  color: '#262626',
  margin: 0,
});

const UnitInfo = styled('p')({
  fontFamily: '"Inter", "Noto Sans Arabic", sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#6f6f6f',
  margin: 0,
  '& strong': {
    fontWeight: 700,
  },
});

const Description = styled('p')({
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#666666',
  margin: 0,
});

const ReasonsContainer = styled(Box)({
  backgroundColor: '#f9fafb',
  borderRadius: '20px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

const ReasonsTitle = styled('p')({
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#262626',
  margin: 0,
});

const ReasonsList = styled('ul')({
  margin: 0,
  padding: 0,
  paddingInlineStart: '21px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  listStyleType: 'disc',
  '& li': {
    fontFamily: '"IBM Plex Sans Arabic", sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#666666',
  },
});

const SolutionContainer = styled(Box)({
  backgroundColor: '#eff6ff',
  border: '1px solid #bedbff',
  borderRadius: '20px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

const SolutionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const SolutionTitle = styled('p')({
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#003d99',
  margin: 0,
});

const SolutionEmoji = styled('span')({
  fontSize: '20px',
});

const SolutionText = styled('p')({
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#003d99',
  margin: 0,
});

const ButtonsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

const PrimaryButton = styled('button')({
  width: '100%',
  height: '48px',
  backgroundColor: '#5b3fff',
  border: 'none',
  borderRadius: '8px',
  color: '#ffffff',
  fontFamily: '"Inter", "Noto Sans Arabic", sans-serif',
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: '22.5px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '#4a2fe6',
  },
});

const SecondaryButton = styled('button')({
  width: '100%',
  height: '44px',
  backgroundColor: '#f2f2f2',
  border: 'none',
  borderRadius: '8px',
  color: '#262626',
  fontFamily: '"Inter", "Noto Sans Arabic", sans-serif',
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: '22.5px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '#e5e5e5',
  },
});

interface MappingErrorModalProps {
  open: boolean;
  onClose: () => void;
  failedResult: IChannelMappingResult | null;
}

const getChannelUrl = (channelType: ChannelType): string => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return 'https://www.airbnb.com/hosting';
    case ChannelType.Booking:
      return 'https://admin.booking.com';
    case ChannelType.Gathern:
      return 'https://host.gathern.co';
    default:
      return '';
  }
};

const MappingErrorModal: React.FC<MappingErrorModalProps> = memo(({
  open,
  onClose,
  failedResult,
}) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  if (!failedResult || !failedResult.error) {
    return null;
  }

  const { error, channelTypeName, listingName, channelType } = failedResult;
  const unitName = listingName || error.unitIdentifier || '';

  // Select content based on user's language
  const title = isArabic ? error.titleAr : error.titleEn;
  const message = isArabic ? error.messageAr : error.messageEn;
  const possibleCauses = isArabic ? error.possibleCausesAr : error.possibleCausesEn;
  const suggestedSolution = isArabic ? error.suggestedSolutionAr : error.suggestedSolutionEn;

  const handleGoToChannel = () => {
    const url = getChannelUrl(channelType);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </CloseButton>

          <IconContainer>
            <ErrorIconWrapper>
              <ErrorOutlineIcon sx={{ fontSize: 28, color: '#e11900' }} />
            </ErrorIconWrapper>
          </IconContainer>

          <MessageContainer>
            <Title>{title || t(T_KEYS.UNITS_MAPPING_MERGE_ERROR_TITLE)}</Title>
            {unitName && (
              <UnitInfo>
                {t(T_KEYS.UNIT_LABEL)}: <strong>{unitName}</strong>
              </UnitInfo>
            )}
            <Description>{message}</Description>
          </MessageContainer>

          {possibleCauses && possibleCauses.length > 0 && (
            <ReasonsContainer>
              <ReasonsTitle>
                {t(T_KEYS.UNITS_MAPPING_POSSIBLE_CAUSES)}
              </ReasonsTitle>
              <ReasonsList>
                {possibleCauses.map((cause, index) => (
                  <li key={index}>{cause}</li>
                ))}
              </ReasonsList>
            </ReasonsContainer>
          )}

          {suggestedSolution && (
            <SolutionContainer>
              <SolutionHeader>
                <SolutionEmoji>💡</SolutionEmoji>
                <SolutionTitle>
                  {t(T_KEYS.UNITS_MAPPING_SUGGESTED_SOLUTION)}
                </SolutionTitle>
              </SolutionHeader>
              <SolutionText>{suggestedSolution}</SolutionText>
            </SolutionContainer>
          )}

          <ButtonsContainer>
            <PrimaryButton onClick={handleGoToChannel}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
              {t(T_KEYS.UNITS_MAPPING_GO_TO_CHANNEL, { channel: channelTypeName })}
            </PrimaryButton>
            <SecondaryButton onClick={onClose}>
              {t(T_KEYS.UNITS_MAPPING_GOT_IT)}
            </SecondaryButton>
          </ButtonsContainer>
        </ModalContainer>
      </ModalOverlay>
    </Modal>
  );
});

MappingErrorModal.displayName = 'MappingErrorModal';

export default MappingErrorModal;
